import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { type ReactNode, useState } from 'react';

import { isControlState } from '../lib/controlStates';
import { US_STATES } from '../util/usStates';

/**
 * Territory state chips, shared by the rep's field view and the admin
 * roster. Behaviors:
 *   - Color-codes control (ABC monopoly) states in purple vs. license
 *     states in gold — matching the depletions views.
 *   - Hovering a chip shows the full state name (and flags control states).
 *   - A short list renders inline; a long list collapses to counts +
 *     "Show all", expanding to a grouped grid (Control / License) so a
 *     near-national territory stays scannable.
 */

const GOLD = '#bb8c3f';
const CONTROL_PURPLE = '#7c3aed';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';
const INLINE_LIMIT = 12;

const STATE_NAMES: ReadonlyMap<string, string> = new Map(US_STATES.map((s) => [s.code, s.name]));

function StateChip({ code }: { code: string }) {
  const ctrl = isControlState(code);
  const name = STATE_NAMES.get(code) ?? code;
  return (
    <Tooltip title={ctrl ? `${name} · Control state` : name} arrow placement="top">
      <Chip
        label={code}
        size="small"
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          letterSpacing: '0.04em',
          cursor: 'default',
          color: ctrl ? CONTROL_PURPLE : GOLD,
          bgcolor: ctrl ? `${CONTROL_PURPLE}14` : 'rgba(187, 140, 63, 0.14)',
          border: `1px solid ${ctrl ? `${CONTROL_PURPLE}55` : 'rgba(187, 140, 63, 0.36)'}`,
        }}
      />
    </Tooltip>
  );
}

function ControlCountPill({ count }: { count: number }) {
  return (
    <Tooltip
      placement="top"
      arrow
      title={
        <Box sx={{ maxWidth: 260 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: CONTROL_PURPLE, mb: 0.25 }}>
            {count} control {count === 1 ? 'state' : 'states'}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'rgba(15, 10, 7, 0.7)', lineHeight: 1.5 }}>
            Shown in purple. These states hold a wholesale / retail monopoly on distilled spirits —
            selling goes through the state liquor authority, not independent distributors.
          </Typography>
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            border: '1px solid rgba(15,10,7,0.12)',
            borderRadius: 1.5,
            px: 1.75,
            py: 1.25,
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          },
        },
        arrow: { sx: { color: '#fff', '&::before': { border: '1px solid rgba(15,10,7,0.12)' } } },
      }}
    >
      <Chip
        size="small"
        icon={<GavelOutlinedIcon sx={{ fontSize: 13 }} />}
        label={`${count} control`}
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: CONTROL_PURPLE,
          bgcolor: `${CONTROL_PURPLE}14`,
          border: `1px solid ${CONTROL_PURPLE}45`,
          cursor: 'help',
          '& .MuiChip-icon': { color: CONTROL_PURPLE },
        }}
      />
    </Tooltip>
  );
}

function StateGroup({
  label,
  accent,
  codes,
  icon,
}: {
  label: string;
  accent: string;
  codes: string[];
  icon?: ReactNode;
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.7, color: accent }}>
        {icon}
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: accent,
          }}
        >
          {label} · {codes.length}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
        {codes.map((c) => (
          <StateChip key={c} code={c} />
        ))}
      </Box>
    </Box>
  );
}

export function TerritoryStates({ states }: { states: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = states.length > INLINE_LIMIT;
  const control = states.filter((s) => isControlState(s)).sort();
  const license = states.filter((s) => !isControlState(s)).sort();

  if (!isLong) {
    return (
      <>
        {states.map((s) => (
          <StateChip key={s} code={s} />
        ))}
        {control.length > 0 && <ControlCountPill count={control.length} />}
      </>
    );
  }

  return (
    <>
      <Chip
        size="small"
        label={`${states.length} states`}
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: GOLD,
          bgcolor: 'rgba(187, 140, 63, 0.24)',
          border: '1px solid rgba(187, 140, 63, 0.36)',
        }}
      />
      {control.length > 0 && <ControlCountPill count={control.length} />}
      <Chip
        size="small"
        clickable
        onClick={(e) => {
          // The rep-roster cards are clickable — don't let expanding the
          // territory also select the rep.
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        label={expanded ? 'Show less' : 'Show all'}
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: GOLD,
          bgcolor: '#fff',
          border: `1px dashed ${GOLD}`,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.08)' },
        }}
      />
      {expanded && (
        <Box
          sx={{
            flexBasis: '100%',
            width: '100%',
            mt: 1.25,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          {control.length > 0 && (
            <StateGroup
              label="Control states"
              accent={CONTROL_PURPLE}
              codes={control}
              icon={<GavelOutlinedIcon sx={{ fontSize: 12 }} />}
            />
          )}
          {license.length > 0 && (
            <StateGroup label="License states" accent={GOLD} codes={license} />
          )}
        </Box>
      )}
    </>
  );
}
